import { ReactElement, useState } from 'react';
import { Page } from '@src/Components/Page';
import { Dropdown } from '../../../Components/Dropdown';
import { Placement } from '../../../Components/designConstants';
import { MenuItem } from '../../../Components/Dropdown/dropdown-constants';
import { useTabbedNav } from '../../../utils/hooks/useNav';
import { ScalingAnalysisTab } from './ScalingAnalysisTab';
import { SCALING_BASE_URL, SCALING_NAVS, ScalingTab } from './scalingConstants';
import { comboOptionItems } from './scaling-run-utils';
import {
  COMBO_SEPARATOR,
  kpiOptions,
  OutcomeKey,
  platformOptions,
  SCALING_FIXTURE,
  splitComboValue,
  tacticOptions,
  findCombo
} from './scalingData';
import './ScalingPage.scss';

const OUTCOME_LABEL: Record<OutcomeKey, string> = { volume: 'Volume', revenue: 'Revenue' };
const OUTCOME_KEY: Record<string, OutcomeKey> = { Volume: 'volume', Revenue: 'revenue' };

interface SelectorProps {
  label: string;
  value: string;
  options: string[] | MenuItem<string>[];
  onChange: (value: string) => void;
}

// All fields render as the same dropdown pill so they match visually — even single-option
// ones (which simply have nothing else to pick).
const Selector = ({ label, value, options, onChange }: SelectorProps): ReactElement => (
  <Dropdown
    overlayPlacement={Placement.RIGHT}
    label={label}
    value={value}
    options={options}
    onChange={onChange}
  />
);

const ScalingPage = (): ReactElement => {
  const { tab, goToTab } = useTabbedNav<string>({
    baseURL: SCALING_BASE_URL,
    defaultKey: ScalingTab.SCALING_ANALYSIS_RESULTS,
    navs: SCALING_NAVS.map(nav => nav.key)
  });

  const run = SCALING_FIXTURE;
  // Cascading selection, all seeded from the first valid combo in the fixture.
  const [platform, setPlatform] = useState<string>(() => platformOptions(run)[0]);
  const [tactic, setTactic] = useState<string>(
    () => tacticOptions(run, platformOptions(run)[0])[0]
  );
  const [kpi, setKpi] = useState<string>(
    () =>
      kpiOptions(run, platformOptions(run)[0], tacticOptions(run, platformOptions(run)[0])[0])[0]
  );
  const [outcome, setOutcome] = useState<OutcomeKey>('volume');

  const platforms = platformOptions(run);
  const combos = comboOptionItems(run);
  const kpis = kpiOptions(run, platform, tactic);
  const comboValue = [run.channel, platform, tactic].join(COMBO_SEPARATOR);

  // The combined Channel:Platform:Tactic dropdown sets platform + tactic together, then resets
  // KPI to the first valid option for the new combo.
  const onCombo = (value: string) => {
    const { platform: p, tactic: t } = splitComboValue(value);
    setPlatform(p);
    setTactic(t);
    setKpi(kpiOptions(run, p, t)[0]);
  };

  const combo = findCombo(run, platform, tactic, kpi);
  const availableOutcomes = (combo ? Object.keys(combo.outcomes) : []) as OutcomeKey[];
  const resolvedOutcome = availableOutcomes.includes(outcome) ? outcome : availableOutcomes[0];
  const outcomeData = combo && resolvedOutcome ? combo.outcomes[resolvedOutcome] : undefined;

  // No data when the scaling_prod.R output (fixture / future endpoint) isn't loaded.
  const hasData = platforms.length > 0;
  const isResultsTab = tab === ScalingTab.SCALING_ANALYSIS_RESULTS;

  let resultsContent: ReactElement;
  if (!hasData) {
    resultsContent = (
      <div className='scaling-empty'>
        No scaling data loaded. Generate the scaling_prod.R output (econ_bigsix_forecasting PR #50)
        and build the local fixture to populate this page.
      </div>
    );
  } else if (outcomeData) {
    resultsContent = (
      <ScalingAnalysisTab
        channel={run.channel}
        platform={platform}
        tactic={tactic}
        kpi={kpi}
        outcomeData={outcomeData}
      />
    );
  } else {
    resultsContent = <div className='scaling-empty'>No scaling data for this selection.</div>;
  }

  return (
    <Page
      title='Scaling'
      pageType='Scaling'
      navs={SCALING_NAVS}
      selectedNav={tab}
      onNav={goToTab}
      subHeader={
        isResultsTab &&
        hasData && (
          <div className='scaling-subheader'>
            <Selector label='Channel' value={comboValue} options={combos} onChange={onCombo} />
            <Selector label='KPI' value={kpi} options={kpis} onChange={setKpi} />
            <Selector
              label='Outcome'
              value={resolvedOutcome ? OUTCOME_LABEL[resolvedOutcome] : ''}
              options={availableOutcomes.map(key => OUTCOME_LABEL[key])}
              onChange={label => setOutcome(OUTCOME_KEY[label])}
            />
          </div>
        )
      }
    >
      <div className='full-page-router scaling-page' data-testid='scaling-page'>
        {tab === ScalingTab.DATA_QA && <div className='scaling-data-qa-tab' />}
        {isResultsTab && resultsContent}
      </div>
    </Page>
  );
};

export { ScalingPage };
